import { useMemo, useRef, useCallback } from 'react'
import type {
  ElevationSample,
  GradeSegment,
  GradeCategory,
} from '../../hooks/useRouteGeometry'

const GRADE_COLOR: Record<GradeCategory, string> = {
  easy: '#22c55e',
  moderate: '#eab308',
  steep: '#f97316',
  extreme: '#ef4444',
}

const VIEWBOX_W = 1000
const VIEWBOX_H = 160
const PAD_LEFT = 44
const PAD_RIGHT = 12
const PAD_TOP = 12
const PAD_BOTTOM = 22

const METERS_TO_FEET = 3.28084
const METERS_TO_MILES = 1 / 1609.344

interface Props {
  profile: ElevationSample[]
  gradeSegments: GradeSegment[]
  /** Current scrubber position in [0, 1] */
  progress: number
  /** Called when the user drags or clicks on the chart, value in [0, 1] */
  onSeek: (t: number) => void
}

export function TripRouteElevationChart({ profile, gradeSegments, progress, onSeek }: Props) {
  const svgRef = useRef<SVGSVGElement>(null)
  const draggingRef = useRef(false)

  const { totalDist, minElev, maxElev, pathD, areaD, hasData } = useMemo(() => {
    if (profile.length < 2) {
      return { totalDist: 0, minElev: 0, maxElev: 0, pathD: '', areaD: '', hasData: false }
    }
    const total = profile[profile.length - 1].distanceMeters
    let min = Infinity
    let max = -Infinity
    for (const s of profile) {
      if (s.elevationMeters < min) min = s.elevationMeters
      if (s.elevationMeters > max) max = s.elevationMeters
    }
    const range = Math.max(1, max - min)
    const xFor = (d: number) =>
      PAD_LEFT + ((d / total) * (VIEWBOX_W - PAD_LEFT - PAD_RIGHT))
    const yFor = (e: number) =>
      PAD_TOP + (1 - (e - min) / range) * (VIEWBOX_H - PAD_TOP - PAD_BOTTOM)

    let p = ''
    let a = ''
    profile.forEach((s, i) => {
      const x = xFor(s.distanceMeters)
      const y = yFor(s.elevationMeters)
      p += i === 0 ? `M${x},${y}` : `L${x},${y}`
    })
    const lastX = xFor(profile[profile.length - 1].distanceMeters)
    const firstX = xFor(profile[0].distanceMeters)
    const baseY = VIEWBOX_H - PAD_BOTTOM
    a = `${p}L${lastX},${baseY}L${firstX},${baseY}Z`

    return { totalDist: total, minElev: min, maxElev: max, pathD: p, areaD: a, hasData: true }
  }, [profile])

  const gradeBands = useMemo(() => {
    if (!hasData || totalDist <= 0) return [] as { x: number; w: number; color: string }[]
    const xFor = (d: number) =>
      PAD_LEFT + ((d / totalDist) * (VIEWBOX_W - PAD_LEFT - PAD_RIGHT))
    return gradeSegments
      .map((seg) => {
        const start = profile[seg.startIdx]?.distanceMeters
        const end = profile[seg.endIdx]?.distanceMeters
        if (start == null || end == null) return null
        const x = xFor(start)
        const w = Math.max(1, xFor(end) - x)
        return { x, w, color: GRADE_COLOR[seg.category] }
      })
      .filter((b): b is { x: number; w: number; color: string } => b !== null)
  }, [gradeSegments, profile, totalDist, hasData])

  const cursorX = useMemo(() => {
    if (!hasData) return PAD_LEFT
    return PAD_LEFT + progress * (VIEWBOX_W - PAD_LEFT - PAD_RIGHT)
  }, [progress, hasData])

  const seekFromEvent = useCallback(
    (clientX: number) => {
      const svg = svgRef.current
      if (!svg) return
      const rect = svg.getBoundingClientRect()
      const xPx = clientX - rect.left
      const xView = (xPx / rect.width) * VIEWBOX_W
      const usable = VIEWBOX_W - PAD_LEFT - PAD_RIGHT
      const t = (xView - PAD_LEFT) / usable
      onSeek(Math.max(0, Math.min(1, t)))
    },
    [onSeek],
  )

  const onPointerDown = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      if (!hasData) return
      draggingRef.current = true
      ;(e.target as Element).setPointerCapture?.(e.pointerId)
      seekFromEvent(e.clientX)
    },
    [hasData, seekFromEvent],
  )
  const onPointerMove = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      if (!draggingRef.current) return
      seekFromEvent(e.clientX)
    },
    [seekFromEvent],
  )
  const onPointerUp = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    draggingRef.current = false
    ;(e.target as Element).releasePointerCapture?.(e.pointerId)
  }, [])

  if (!hasData) {
    return (
      <div className="w-full h-[140px] flex items-center justify-center text-white/40 text-xs">
        Elevation profile unavailable
      </div>
    )
  }

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${VIEWBOX_W} ${VIEWBOX_H}`}
      preserveAspectRatio="none"
      className="w-full h-[140px] touch-none select-none cursor-pointer"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      aria-label="Elevation profile — drag to scrub"
    >
      {/* Grade-colored bands behind the line */}
      {gradeBands.map((b, i) => (
        <rect
          key={i}
          x={b.x}
          y={PAD_TOP}
          width={b.w}
          height={VIEWBOX_H - PAD_TOP - PAD_BOTTOM}
          fill={b.color}
          opacity={0.18}
        />
      ))}

      {/* Filled elevation area */}
      <path d={areaD} fill="rgba(255,255,255,0.08)" />
      {/* Elevation line */}
      <path d={pathD} fill="none" stroke="#ffffff" strokeWidth={1.5} strokeLinejoin="round" />

      {/* Y-axis labels — elevation shown in feet */}
      <text x={4} y={PAD_TOP + 8} fill="rgba(255,255,255,0.55)" fontSize={10}>
        {Math.round(maxElev * METERS_TO_FEET).toLocaleString()} ft
      </text>
      <text x={4} y={VIEWBOX_H - PAD_BOTTOM - 2} fill="rgba(255,255,255,0.55)" fontSize={10}>
        {Math.round(minElev * METERS_TO_FEET).toLocaleString()} ft
      </text>
      {/* X-axis labels — distance shown in miles */}
      <text x={PAD_LEFT} y={VIEWBOX_H - 6} fill="rgba(255,255,255,0.55)" fontSize={10}>
        0 mi
      </text>
      <text
        x={VIEWBOX_W - PAD_RIGHT}
        y={VIEWBOX_H - 6}
        fill="rgba(255,255,255,0.55)"
        fontSize={10}
        textAnchor="end"
      >
        {(totalDist * METERS_TO_MILES).toFixed(1)} mi
      </text>

      {/* Scrubber cursor */}
      <line
        x1={cursorX}
        x2={cursorX}
        y1={PAD_TOP}
        y2={VIEWBOX_H - PAD_BOTTOM}
        stroke="#ffffff"
        strokeWidth={2}
        opacity={0.9}
      />
      <circle cx={cursorX} cy={PAD_TOP} r={4} fill="#ffffff" />
    </svg>
  )
}
